import React from "react"
import styled, { css } from "styled-components"
import Link from "next/link"
import { rem } from "polished"
import { Breakpoint, theme } from "../../util/styles/theme"

export interface NavigationLinkProps {
  active: boolean
  href: string
  subLink?: boolean
  visibleFrom?: Breakpoint // Inclusive
  visibleTo?: Breakpoint // Exclusive
}

export const halfNavigationLinkSpacingPx = 16

interface ContainerProps {
  active: boolean
  subLink: boolean | undefined
  visibleFrom?: Breakpoint
  visibleTo?: Breakpoint
}

export const Container = styled.li<ContainerProps>`
  ${theme.textStyle("default", "medium", "semibold")}

  ${({ subLink }) =>
    subLink
      ? theme.textStyle("default", "small", "semibold")
      : theme.textStyle("default", "medium", "semibold")}

  a:link,
  a:visited,
  a:active {
    color: ${theme.colors.primary};
    display: inline-block;
    letter-spacing: ${rem(0.5)};
    padding: ${() =>
      `${rem(halfNavigationLinkSpacingPx / 2)} ${rem(
        halfNavigationLinkSpacingPx,
      )}`};
    position: relative;
    text-decoration: none;
    text-transform: uppercase;
    white-space: nowrap;
  }

  ${({ active }) =>
    active &&
    css`
      a:link,
      a:visited,
      a:active {
        color: ${theme.colors.orange};
      }
    `}

  a:hover,
  a:focus {
    color: ${theme.colors.primary};
    opacity: 50%;
  }

  padding-top: ${theme.spacings.small};
  padding-bottom: ${theme.spacings.small};
  margin-top: ${theme.spacings.small};
  margin-bottom: ${theme.spacings.small};

  ${({ visibleFrom }) => {
    if (!visibleFrom) {
      return undefined
    }

    return css`
      display: none;

      ${theme.breakpoints[visibleFrom]} {
        display: unset;
      }
    `
  }}

  ${({ visibleTo }) => {
    if (!visibleTo) {
      return undefined
    }

    return css`
      ${theme.breakpoints[visibleTo]} {
        display: none;
      }
    `
  }}

  ${theme.breakpoints.md} {
    padding-top: 0;
    padding-bottom: 0;
    margin-top: 0;
    margin-bottom: 0;
  }
`

export const NavigationLink: React.FC<NavigationLinkProps> = ({
  active,
  children,
  href,
  subLink,
  visibleFrom,
  visibleTo,
}) => (
  <Container
    active={active}
    subLink={subLink}
    visibleFrom={visibleFrom}
    visibleTo={visibleTo}
  >
    <Link href={href}>{children}</Link>
  </Container>
)
